import { useState, useCallback } from 'react';
import { apiService } from '../services/api';
import type { FeatureFlagDto, CreateFeatureFlagRequest } from '../services/api';
import { getApiErrorMessage } from '../types/api-errors';

export const useFeatureFlags = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [flags, setFlags] = useState<FeatureFlagDto[]>([]);

    const fetchFlags = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiService.getFeatureFlags();
            setFlags(res);
            return res;
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Unable to load feature flags.');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const toggleFlag = useCallback(async (id: number, enabled: boolean) => {
        setLoading(true);
        setError(null);
        try {
            await apiService.toggleFeatureFlag(id, enabled);
            setFlags(prev => prev.map(f => f.id === id ? { ...f, enabled } : f));
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to toggle feature flag.');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const createFlag = useCallback(async (data: CreateFeatureFlagRequest) => {
        setLoading(true);
        setError(null);
        try {
            const newFlag = await apiService.createFeatureFlag(data);
            setFlags(prev => [...prev, newFlag]);
            return newFlag;
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to create feature flag.');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    return { loading, error, flags, fetchFlags, toggleFlag, createFlag };
};
